import { inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ApiService } from '../../core/services/api.service';
import { buildApiUrl } from '../../core/config/api.constants';
import { createEntityStore } from '../../core/utils/entity-store.util';
import type { CatalogueImageUploadResponse, CatalogueItem } from './catalogue.types';

export interface CatalogueStoreOptions {
  /** With trailing slash, from API_ENDPOINTS: '/api/weapons/'. */
  endpoint: string;
  /** '/api/weapons/upload_image'. */
  uploadPath: string;
}

/**
 * One unpaginated httpResource list plus the mutations every shooting catalogue
 * exposes. Must be called in an injection context (a service field initializer).
 */
export function createCatalogueStore<T extends CatalogueItem, TCreate, TUpdate>(options: CatalogueStoreOptions) {
  const apiService = inject(ApiService);
  const store = createEntityStore<T, TCreate, TUpdate>({ endpoint: options.endpoint });

  return {
    resource: store.resource,
    items: store.data,
    loading: store.loading,
    error: store.error,
    byId: store.byId,
    reload: store.reload,
    create: store.create,
    update: store.update,
    /**
     * The backend deletes at `/{id}/`, not the `/id/{id}` shape ApiService.delete()
     * (and therefore createEntityStore's bundled `remove`) assumes; the kit's `remove`
     * would 404.
     */
    remove: (id: number): Observable<void> =>
      apiService.customDelete<{ detail: string }>(buildApiUrl(`${options.endpoint}${id}/`)).pipe(
        map(() => undefined),
        tap(() => store.reload()),
      ),
    uploadImage: (file: File): Observable<CatalogueImageUploadResponse> =>
      apiService.uploadFile<CatalogueImageUploadResponse>(options.uploadPath, file),
  };
}

export type CatalogueStoreShape<T extends CatalogueItem, TCreate, TUpdate> = ReturnType<
  typeof createCatalogueStore<T, TCreate, TUpdate>
>;
